/* cz-popup.js — opens the newsletter modal, once, and remembers that it did.
 *
 * The rules a popup has to follow before it is allowed to exist on a store like this one:
 *
 *   NEVER interrupt intent. It does not open on /cart or /checkout, and it does not open while
 *   the cart drawer is open — someone reaching for the buy button is not a lead to capture.
 *   ONCE. Signed up: never again. Dismissed: not for `data-days` days. Both in localStorage,
 *   wrapped in try/catch because private mode throws on write.
 *   EARN THE INTERRUPTION. It waits for a delay OR a scroll depth OR an exit gesture — some
 *   signal the visitor is actually reading — rather than firing on load.
 *   NATIVE SEMANTICS. <dialog>.showModal() gives the focus trap, the Escape key, the inert
 *   background and the ::backdrop for free, and gets them right in a way hand-rolled modals
 *   usually do not.
 *
 * If this script never runs, the <dialog> simply stays closed. A dialog without an open
 * attribute is display:none by UA stylesheet, so there is no flash and nothing to clean up.
 */
(function () {
  'use strict';

  var el = document.querySelector('[data-cz-popup]');
  if (!el || typeof el.showModal !== 'function') return;

  var KEY_DONE = 'cz.pop.done';       // signed up — never show again
  var KEY_SNOOZE = 'cz.pop.snooze';   // dismissed — show again after N days

  function get(k) { try { return window.localStorage.getItem(k); } catch (e) { return null; } }
  function set(k, v) { try { window.localStorage.setItem(k, v); } catch (e) {} }

  // ---- came back from a successful submit? confirm, and never ask again.
  var params = new URLSearchParams(window.location.search);
  if (params.get('cz_pop') === 'ok' || params.get('customer_posted') === 'true') {
    set(KEY_DONE, '1');
  }

  if (get(KEY_DONE)) return;

  var snooze = parseInt(get(KEY_SNOOZE) || '0', 10);
  if (snooze && Date.now() < snooze) return;

  // ---- never on the paths where the visitor is trying to buy
  var p = window.location.pathname;
  if (/\/(cart|checkout|challenge|account)/.test(p)) return;
  // ...and not on a product page. The docstring above says "NEVER interrupt intent", but the
  // path test only covered the cart: measured on the wallet PDP the dialog opened at 12.1s at
  // scrollY 0 and, being a real <dialog> modal, blocked every click on the colour swatches and
  // Add to bag until dismissed (gate G3 round 2). Someone reading a product page is further
  // down the funnel than someone on /cart, not further up.
  if (/\/products\//.test(p) || document.body.classList.contains('template-product')) return;

  var delay = (parseInt(el.getAttribute('data-delay'), 10) || 12) * 1000;
  var scrollPct = parseInt(el.getAttribute('data-scroll'), 10) || 35;
  var days = parseInt(el.getAttribute('data-days'), 10) || 60;

  var opened = false;
  var timer = null;

  function cartDrawerOpen() {
    var d = document.querySelector('.cz-drawer[open], [data-cz-drawer][open], dialog[open]:not([data-cz-popup])');
    return !!d;
  }

  function open() {
    if (opened || !document.body.contains(el)) return;
    if (cartDrawerOpen()) return;         // do not stack on top of the bag
    opened = true;
    cleanup();
    try { el.showModal(); } catch (e) { return; }
    var input = el.querySelector('input[type=email]');
    // Focus the field, but not on a phone — showing the keyboard immediately is hostile.
    if (input && window.matchMedia('(min-width: 700px)').matches) {
      try { input.focus({ preventScroll: true }); } catch (e) { input.focus(); }
    }
  }

  function snoozeNow() { set(KEY_SNOOZE, String(Date.now() + days * 864e5)); }

  function onScroll() {
    var h = document.documentElement.scrollHeight - window.innerHeight;
    if (h <= 0) return;
    if ((window.scrollY / h) * 100 >= scrollPct) open();
  }

  // Exit intent, desktop only: a fast move toward the top edge of the window.
  function onOut(e) {
    if (e.clientY <= 0 && !e.relatedTarget) open();
  }

  function cleanup() {
    if (timer) { window.clearTimeout(timer); timer = null; }
    window.removeEventListener('scroll', onScroll);
    document.removeEventListener('mouseout', onOut);
  }

  timer = window.setTimeout(open, delay);
  window.addEventListener('scroll', onScroll, { passive: true });
  if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
    document.addEventListener('mouseout', onOut);
  }

  // Dismissal — both the X (method="dialog") and the "no thanks" link land here.
  el.addEventListener('close', snoozeNow);
  var no = el.querySelector('[data-cz-popup-dismiss]');
  if (no) no.addEventListener('click', function () { snoozeNow(); el.close(); });

  // Clicking the backdrop closes it. Without this the only way out on desktop is the X, which
  // is the behaviour that makes people hate modals.
  el.addEventListener('click', function (e) {
    if (e.target === el) { snoozeNow(); el.close(); }
  });

  // A submit is a conversion: remember it before the page navigates away, so the modal cannot
  // reappear in the window between POST and redirect.
  var form = el.querySelector('form[action]');
  if (form) form.addEventListener('submit', function () { set(KEY_DONE, '1'); });
})();
